import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Autocomplete, createFilterOptions, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Paper, Stack, TextField, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProjectConflictItem, ProjectDocumentItem, ProjectListItem, ProjectsClient } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';
import { Permissions, usePermissions } from '../../contexts/PermissionContext';
import PagedItems from '../shared/PagedItems';
import LoadingWheel from '../shared/LoadingWheel';
import { StatusChip } from '../shared/StatusChip';

interface Props { projectId: string; projectReference: string; documents: ProjectDocumentItem[]; onResolved: () => void; }

const ProjectConflictsPanel: React.FC<Props> = ({ projectId, projectReference, documents, onResolved }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const client = useMemo(() => new ProjectsClient(getApiBaseUrl(), authenticatedFetch), []);
  const [items, setItems] = useState<ProjectConflictItem[]>([]);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [decision, setDecision] = useState('KeepTogether');
  const [notes, setNotes] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [destination, setDestination] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewed, setViewed] = useState<string | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const documentNames = useMemo(() => new Map(documents.map(d => [d.documentId, d.fileName])), [documents]);
  const documentFilter = useMemo(() => createFilterOptions<ProjectDocumentItem>({ limit: 50 }), []);
  const metadataKeys = ['document_name', 'source_archive', 'association_basis', 'grouping_signal'];
  const sourceEvidence = (item: ProjectConflictItem) => (item.evidence ?? []).filter(e => !metadataKeys.includes(e.fieldKey ?? '') && !e.fieldKey?.startsWith('grouping_signal_values.'));
  const sourceIds = (item: ProjectConflictItem) => Array.from(new Set(sourceEvidence(item).map(e => e.documentId).filter((id): id is string => !!id)));
  const fileName = (item: ProjectConflictItem, id: string) => item.evidence?.find(e => e.documentId === id && e.fieldKey === 'document_name')?.value
    ?? documentNames.get(id) ?? t('projects.viewEvidence');
  const explanation = (item: ProjectConflictItem) => {
    if (item.rule === 'AssociationReview') {
      const rule = item.evidence?.find(e => e.fieldKey === 'grouping_rule')?.value;
      return t(`projects.associationReasons.${rule}`, { defaultValue: t('projects.associationReasons.unknown') });
    }
    if (item.rule?.startsWith('Inconsistent.')) return t('projects.inconsistentExplanation', { field: t(`projects.fields.${item.rule.substring('Inconsistent.'.length)}`) });
    if (item.rule?.startsWith('HeaderMismatch.')) return t('projects.headerMismatchExplanation', { field: t(`projects.fields.${item.rule.substring('HeaderMismatch.'.length)}`) });
    return t(`projects.ruleDescriptions.${item.rule}`, { defaultValue: t('projects.reviewRequired') });
  };
  useEffect(() => {
    if (!selected || decision !== 'MoveDocument') return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const query = new URLSearchParams({ pageSize: '50', search: projectSearch });
        const response = await authenticatedFetch.fetch(`${getApiBaseUrl() ?? ''}/api/projects?${query}`, { signal: controller.signal });
        if (!response.ok) throw new Error();
        const result = await response.json();
        if (!controller.signal.aborted) setProjects((result.items ?? []).filter((p: ProjectListItem) => p.id !== projectId));
      } catch { if (!controller.signal.aborted) setError(true); }
      finally { if (!controller.signal.aborted) setSearching(false); }
    }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [selected, decision, projectSearch, projectId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try { setItems(await client.getApiProjectsConflicts(projectId)); }
    catch { setError(true); }
    finally { setLoading(false); }
  }, [client, projectId]);
  useEffect(() => { void load(); }, [load]);

  const beginResolution = (id: string) => {
    setSelected(id); setDecision('KeepTogether'); setNotes(''); setReviewed(false); setDocumentId(''); setDestination(''); setProjectSearch(''); setProjects([]);
  };

  const resolve = async (item: ProjectConflictItem) => {
    if (!item.id || !item.version) return;
    setSaving(true); setError(false);
    try {
      await client.postApiProjectsConflictsResolve(projectId, item.id, {
        expectedVersion: item.version, decision, notes: notes.trim(),
        documentId: decision === 'KeepTogether' ? undefined : documentId,
        destinationProjectId: decision === 'MoveDocument' ? destination : undefined,
      });
      setSelected(null); await load(); onResolved();
    } catch { setError(true); }
    finally { setSaving(false); }
  };

  if (loading) return <LoadingWheel />;
  return <Stack spacing={6}>
    {error && <Alert severity="error" action={<Button onClick={() => { setSelected(null); void load(); }}>{t('projects.reload')}</Button>}>{t('projects.requestFailed')}</Alert>}
    {!error && items.length === 0 && <Alert severity="info">{t('projects.noFindings')}</Alert>}
    {items.length > 0 && <PagedItems items={items}>{pageItems => <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead><TableRow>
          <TableCell>{t('projects.document')}</TableCell><TableCell>{t('projects.finding')}</TableCell><TableCell>{t('projects.statusLabel')}</TableCell>
          <TableCell>{t('projects.evidenceCount')}</TableCell><TableCell>{t('projects.detectedLabel')}</TableCell><TableCell />
        </TableRow></TableHead>
        <TableBody>{pageItems.map(item => <TableRow key={item.id} selected={viewed === item.id}>
          <TableCell sx={{ maxWidth: 360, overflowWrap: 'anywhere' }}>
            {sourceIds(item).length === 0 ? t('projects.projectLevelFinding') : <PagedItems items={sourceIds(item)} pageSize={5}>{ids => ids.map(id => <Typography key={id} variant="textSM">{fileName(item, id)}</Typography>)}</PagedItems>}
          </TableCell>
          <TableCell><Typography>{t(`projects.rules.${item.rule}`, { defaultValue: t('projects.reviewRequired') })}</Typography>
            <Typography variant="textSM" color="text.secondary">{explanation(item)}</Typography>
          </TableCell>
          <TableCell><StatusChip label={t(item.resolution ? 'projects.resolved' : item.isActive ? 'projects.open' : 'projects.noLongerActive')} tone={item.isActive && !item.resolution ? 'warning' : 'default'} /></TableCell>
          <TableCell>{sourceEvidence(item).length}</TableCell>
          <TableCell>{new Date(item.detectedAt ?? '').toLocaleString()}</TableCell>
          <TableCell><Button size="md" disabled={saving} onClick={() => { setViewed(item.id ?? null); setSelected(null); }}>{t('projects.viewEvidence')}</Button></TableCell>
        </TableRow>)}</TableBody>
      </Table>
    </TableContainer>}</PagedItems>}
    {items.filter(item => item.id === viewed).map(item => <Dialog key={item.id} open fullWidth maxWidth="lg" onClose={() => { if (!saving) { setViewed(null); setSelected(null); } }} aria-labelledby="conflict-evidence-title">
      <DialogTitle id="conflict-evidence-title">{t(`projects.rules.${item.rule}`, { defaultValue: t('projects.reviewRequired') })}</DialogTitle>
      <DialogContent dividers>
      <Stack direction="row" justifyContent="space-between" spacing={6}>
        <Typography variant="textLG">{t(`projects.rules.${item.rule}`, { defaultValue: t('projects.reviewRequired') })}</Typography>
        <StatusChip label={t(item.resolution ? 'projects.resolved' : item.isActive ? 'projects.open' : 'projects.noLongerActive')}
          tone={item.isActive && !item.resolution ? 'warning' : 'default'} />
      </Stack>
      <Typography variant="textSM" color="text.secondary" sx={{ my: 4 }}>{t('projects.detected', { date: new Date(item.detectedAt ?? '').toLocaleString() })}</Typography>
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography>{explanation(item)}</Typography>
        {item.evidence?.some(e => e.fieldKey === 'association_basis' && e.value === 'ArchivePack') && <Typography>
          {t('projects.archiveAssociation', { archive: item.evidence?.find(e => e.fieldKey === 'source_archive')?.value, project: projectReference })}
        </Typography>}
        {item.evidence?.some(e => e.fieldKey === 'grouping_signal') && <Typography>
          {t('projects.conflictingPackSignals', { fields: Array.from(new Set(item.evidence.filter(e => e.fieldKey === 'grouping_signal').map(e => t(`projects.fields.${e.value}`)))).join(', ') })}
        </Typography>}
        {item.evidence?.filter(e => e.fieldKey?.startsWith('grouping_signal_values.')).map((e, index) => <Typography key={`${e.fieldKey}-${index}`} sx={{ overflowWrap: 'anywhere' }}>
          {t(`projects.fields.${e.fieldKey?.substring('grouping_signal_values.'.length)}`)}: {e.value}
        </Typography>)}
      </Alert>
      <Stack spacing={4}>
        <PagedItems items={sourceEvidence(item)}>{evidence => evidence.map((e, index) => <Stack key={`${e.fieldId ?? e.fieldKey}-${index}`} direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems={{ md: 'center' }}>
          <Typography sx={{ overflowWrap: 'anywhere' }}>{t(`projects.fields.${e.fieldKey}`, { defaultValue: e.fieldKey })}: {e.fieldKey === 'upload_batch' ? t('projects.uploadPack') : e.fieldKey === 'grouping_rule' ? t(`groupingRules.${e.value}`) : e.value}</Typography>
          {e.documentId && <Button size="md" component={RouterLink} to={`/review/${e.documentId}${e.fieldId ? `?field=${e.fieldId}` : ''}`}>
            {fileName(item, e.documentId)}
          </Button>}
        </Stack>)}</PagedItems>
      </Stack>
      {item.relatedProjectId && <Button size="md" component={RouterLink} to={`/projects/${item.relatedProjectId}`}>{t('projects.relatedProject')}</Button>}
      {item.resolution && <Alert severity="success" sx={{ mt: 6 }}>
        <Typography>{t(`projects.decisions.${item.resolution.decision}`)}</Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.resolution.notes}</Typography>
        <Typography variant="textSM">{t('projects.resolvedBy', { actor: item.resolution.actorName || t('projects.formerUser'), date: new Date(item.resolution.resolvedAt ?? '').toLocaleString() })}</Typography>
      </Alert>}
      {item.isActive && !item.resolution && hasPermission(Permissions.CONFLICT_RESOLVE) && (selected !== item.id
        ? <Button size="md" variant="secondary" sx={{ mt: 6 }} onClick={() => void beginResolution(item.id!)}>{t('projects.resolve')}</Button>
        : <Stack spacing={6} sx={{ mt: 8 }}>
          <TextField select label={t('projects.decision')} value={decision} onChange={e => setDecision(e.target.value)} disabled={saving}>
            <MenuItem value="KeepTogether">{t('projects.decisions.KeepTogether')}</MenuItem>
            {hasPermission(Permissions.DOCUMENT_REFILE) && <MenuItem value="MoveDocument">{t('projects.decisions.MoveDocument')}</MenuItem>}
            {hasPermission(Permissions.DOCUMENT_REFILE) && hasPermission(Permissions.PROJECT_MANAGE) && <MenuItem value="CreateProject">{t('projects.decisions.CreateProject')}</MenuItem>}
          </TextField>
          {decision !== 'KeepTogether' && <Autocomplete
            options={documents} filterOptions={documentFilter} disabled={saving}
            value={documents.find(d => d.documentId === documentId) ?? null}
            getOptionLabel={d => d.fileName ?? ''} isOptionEqualToValue={(a, b) => a.documentId === b.documentId}
            onChange={(_, value) => setDocumentId(value?.documentId ?? '')}
            renderOption={(props, option) => <li {...props} key={option.documentId}>{option.fileName}</li>}
            renderInput={params => <TextField {...params} label={t('projects.document')} />} />}
          {decision === 'MoveDocument' && <Autocomplete
            options={projects} filterOptions={options => options} disabled={saving} loading={searching}
            value={projects.find(p => p.id === destination) ?? null}
            getOptionLabel={p => [p.projectNumber ?? p.internalReference, p.vesselName, p.externalReference, p.cargoDescription].filter(Boolean).join(' · ')}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { setProjectSearch(value); setDestination(''); } }}
            onChange={(_, value) => setDestination(value?.id ?? '')}
            renderInput={params => <TextField {...params} label={t('projects.destination')} />} />}
          <TextField label={t('projects.notes')} required multiline minRows={3} value={notes} onChange={e => setNotes(e.target.value)} inputProps={{ maxLength: 2000 }} disabled={saving} />
          <FormControlLabel control={<Checkbox checked={reviewed} onChange={e => setReviewed(e.target.checked)} disabled={saving} />} label={t('projects.reviewedEvidence')} />
          <Typography variant="textSM" color="text.secondary">{t('projects.auditHint')}</Typography>
          <Stack direction="row" spacing={4}>
            <Button size="md" variant="primary" disabled={saving || !reviewed || !notes.trim() || (decision !== 'KeepTogether' && !documentId) || (decision === 'MoveDocument' && !destination)} onClick={() => void resolve(item)}>{t('projects.recordResolution')}</Button>
            <Button size="md" disabled={saving} onClick={() => setSelected(null)}>{t('common.cancel')}</Button>
          </Stack>
        </Stack>)}
      </DialogContent>
      <DialogActions><Button size="md" disabled={saving} onClick={() => { setViewed(null); setSelected(null); }}>{t('projects.closeEvidence')}</Button></DialogActions>
    </Dialog>)}
  </Stack>;
};

export default ProjectConflictsPanel;
